import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Search,
  Plus,
  Music,
  Users,
  Calendar,
  AlertCircle,
  Loader2,
  Grid,
  Edit,
  Trash,
  List,
  Timer,
  ArrowUpDown,
  SortAsc,
  ArrowDownUp,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { fetchAuthSession } from "@aws-amplify/auth";
import { useTheme } from "../../context/ThemeContext";
import { ROUTINES_API, ROSTER_API } from "../../utils/api";
import { fetchWithAuth } from "../../utils/api";

import DeleteModal from "../../components/routines_user/DeleteModal";
import RoutineCard from "../../components/routines_user/RoutineCard";
import RoutineFormModal from "../../components/routines_user/RoutineFormModal";
import BulkDancerModal from "../../components/routines_user/BulkDancerModal";
import { SkeletonRoutineCard } from "../../components/ui/SkeletonLoader";
import { SkeletonStatCard } from "../../components/ui/SkeletonLoader";

// 🔄 PERFORMANCE: Module-level deduplication cache for API calls
// This prevents React Strict Mode from causing duplicate API calls
let routinesPageDataCache = null;
let routinesPageDataPromise = null;

// Helper function to normalize roles
const normalizeRoles = (roleValue) => {
  if (!roleValue) return [];

  if (Array.isArray(roleValue) && typeof roleValue[0] === "string") {
    return roleValue;
  }
  if (
    Array.isArray(roleValue) &&
    typeof roleValue[0] === "object" &&
    "S" in roleValue[0]
  ) {
    return roleValue.map((r) => r.S);
  }
  if (typeof roleValue === "string") {
    return [roleValue];
  }

  return [];
};

// Helper to get initials from first and last name
const getInitials = (firstName, lastName) =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();

// Helper to format time from seconds to mm:ss
const formatTime = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

// Custom hook for audio duration
const useAudioDuration = (songFileUrl) => {
  const [duration, setDuration] = useState(null);

  useEffect(() => {
    if (!songFileUrl) {
      setDuration(null);
      return;
    }

    const audio = new Audio(songFileUrl);
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("error", () => {
      console.error("Error loading audio file:", songFileUrl);
      setDuration(null);
    });

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.pause();
    };
  }, [songFileUrl]);

  return duration;
};

// Table Row Component
const RoutineTableRow = ({ routine, roster, onEdit, onDelete, isDarkMode }) => {
  const dancers = roster.filter((member) =>
    routine.dancers?.includes(member.dancerId)
  );
  const duration = useAudioDuration(routine.songFileUrl);
  const hasSong = Boolean(routine.songFileName);

  return (
    <>
      {/* Title */}
      <td className={`${isDarkMode ? "text-gray-400" : ""}`}>
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={`font-medium truncate max-w-[200px] group-hover:text-primary transition-colors ${
              isDarkMode ? "group-hover:text-blue-400" : ""
            }`}
          >
            {routine.title}
          </div>
          {!hasSong && (
            <div
              className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                isDarkMode
                  ? "bg-red-500/10 text-red-400"
                  : "bg-error/10 text-error"
              } group-hover:bg-opacity-100 transition-all`}
            >
              Missing Music
            </div>
          )}
        </div>
      </td>

      {/* Dancers with avatars */}
      <td
        className={`${
          isDarkMode ? "text-gray-400" : ""
        } transition-colors group-hover:bg-opacity-75`}
      >
        <div className="flex items-center gap-2">
          <div className="flex -space-x-3 mr-2">
            {dancers.slice(0, 3).map((dancer, index) => (
              <div
                key={dancer.dancerId}
                className={`
                  w-8 h-8 rounded-full border-2
                  ${isDarkMode ? "border-[#1a1f2d]" : "border-white"}
                `}
                style={{
                  zIndex: dancers.length - index,
                }}
              >
                {dancer.profilePicture ? (
                  <img
                    src={dancer.profilePicture}
                    alt={`${dancer.firstName} ${dancer.lastName}`}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <div
                    className={`
                      w-full h-full rounded-full flex items-center justify-center 
                      text-xs font-medium 
                      ${
                        isDarkMode
                          ? "bg-[#252D42] text-gray-300"
                          : "bg-base-300"
                      }
                    `}
                  >
                    {getInitials(dancer.firstName, dancer.lastName)}
                  </div>
                )}
              </div>
            ))}
            {dancers.length > 3 && (
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center 
                  text-xs font-medium border-2
                  ${
                    isDarkMode
                      ? "bg-[#252D42] border-[#1a1f2d] text-gray-300"
                      : "bg-base-300 border-white"
                  }
                `}
              >
                +{dancers.length - 3}
              </div>
            )}
          </div>
          <span className="group-hover:font-medium transition-all">
            {dancers.length} Dancers
          </span>
        </div>
      </td>

      {/* Average Age */}
      <td
        className={`${
          isDarkMode ? "text-gray-400" : ""
        } group-hover:font-medium transition-all`}
      >
        {routine.averageAge || "-"}
      </td>

      {/* Song */}
      <td className={isDarkMode ? "text-gray-400" : ""}>
        <div className="flex items-center gap-2">
          {hasSong ? (
            <>
              <Music
                className={`h-4 w-4 ${
                  isDarkMode ? "text-green-400" : "text-success"
                } group-hover:scale-110 transition-transform`}
              />
              <span className="truncate max-w-[200px] group-hover:font-medium transition-all">
                {routine.songFileName}
              </span>
            </>
          ) : (
            <div
              className={`flex items-center gap-2 ${
                isDarkMode ? "text-red-400" : "text-error"
              }`}
            >
              <AlertCircle className="h-4 w-4 group-hover:scale-110 transition-transform" />
              <span className="truncate max-w-[200px] font-medium">
                No music added
              </span>
            </div>
          )}
        </div>
      </td>

      {/* Duration */}
      <td className={isDarkMode ? "text-gray-400" : ""}>
        <div className="flex items-center gap-2">
          <Timer
            className={`h-4 w-4 ${
              isDarkMode
                ? hasSong
                  ? "text-blue-400"
                  : "text-red-400"
                : hasSong
                ? "text-primary"
                : "text-error"
            } group-hover:scale-110 transition-transform`}
          />
          <span
            className={`${
              hasSong ? "" : `${isDarkMode ? "text-red-400" : "text-error"}`
            } group-hover:font-medium transition-all`}
          >
            {formatTime(duration)}
          </span>
        </div>
      </td>

      {/* Actions */}
      <td className={isDarkMode ? "text-gray-400" : ""}>
        <div className="flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(routine);
            }}
            className={`btn btn-ghost btn-sm ${
              isDarkMode
                ? "text-gray-400 hover:text-blue-400"
                : "hover:text-primary"
            } transition-colors`}
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(routine);
            }}
            className={`btn btn-ghost btn-sm ${
              isDarkMode
                ? "text-gray-400 hover:text-red-400"
                : "hover:text-error"
            } transition-colors`}
          >
            <Trash className="h-4 w-4" />
          </button>
        </div>
      </td>
    </>
  );
};

const RoutinesPage = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const [routines, setRoutines] = useState([]);
  const [roster, setRoster] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [selectedRoutines, setSelectedRoutines] = useState(new Set());
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDancerModalOpen, setIsBulkDancerModalOpen] = useState(false);
  const [bulkDancerMode, setBulkDancerMode] = useState("add"); // "add" or "remove"
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "title",
    direction: "asc"
  });
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [audioDurations, setAudioDurations] = useState({});

  // For pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Initialize new routine template
  const newRoutineTemplate = {
    title: "",
    style: "",
    level: "",
    dancers: [],
    audioFile: null,
  };

  // 🔄 PERFORMANCE: Fetch data with deduplication to prevent React Strict Mode duplicate calls
  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Use cached data if available
      if (routinesPageDataCache) {
        console.log('🔄 Using cached routines page data');
        setRoutines(routinesPageDataCache.routines || []);
        setRoster(routinesPageDataCache.roster || []);
        setIsLoading(false);
        return;
      }

      // Return existing promise if already fetching
      if (routinesPageDataPromise) {
        console.log('🔄 Joining existing routines page data fetch');
        const cachedResult = await routinesPageDataPromise;
        setRoutines(cachedResult.routines || []);
        setRoster(cachedResult.roster || []);
        setIsLoading(false);
        return;
      }

      // Create new fetch promise
      console.log('🔄 Starting fresh routines page data fetch');
      routinesPageDataPromise = Promise.all([
        fetchWithAuth(ROUTINES_API),
        fetchWithAuth(ROSTER_API),
      ]).then(([routinesData, rosterData]) => {
        const result = {
          routines: routinesData.routines || [],
          roster: rosterData.roster || []
        };
        
        // Cache the result
        routinesPageDataCache = result;
        
        // Clear the promise
        routinesPageDataPromise = null;
        
        console.log(`✅ Fetched ${result.routines.length} routines and ${result.roster.length} roster members`);
        return result;
      });

      const result = await routinesPageDataPromise;
      setRoutines(result.routines);
      setRoster(result.roster);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.toString());
      // Clear cache on error
      routinesPageDataCache = null;
      routinesPageDataPromise = null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate the number of dancers in roster using normalized roles
  const activeDancers = useMemo(() => {
    return roster.filter((member) => {
      const roles = normalizeRoles(member.role);
      return roles.includes("Dancer");
    }).length;
  }, [roster]);

  // Update durations when routines change
  useEffect(() => {
    routines.forEach(routine => {
      if (routine.songFileUrl) {
        const audio = new Audio(routine.songFileUrl);
        audio.addEventListener('loadedmetadata', () => {
          setAudioDurations(prev => ({
            ...prev,
            [routine.routineId]: audio.duration
          }));
        });
        audio.addEventListener('error', () => {
          console.error('Error loading audio:', routine.songFileUrl);
          setAudioDurations(prev => ({
            ...prev,
            [routine.routineId]: 0
          }));
        });
      }
    });
  }, [routines]);

  // Filter and sort routines
  const filteredRoutines = useMemo(() => {
    return routines
      .filter(routine => {
        const searchLower = searchQuery.toLowerCase();
        return routine.title?.toLowerCase().includes(searchLower);
      })
      .sort((a, b) => {
        if (!sortConfig.key) return 0;

        let aValue, bValue;

        switch (sortConfig.key) {
          case 'title':
            aValue = (a.title || '').toLowerCase().trim();
            bValue = (b.title || '').toLowerCase().trim();
            return sortConfig.direction === 'asc' 
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);

          case 'dancers':
            aValue = a.dancers?.length || 0;
            bValue = b.dancers?.length || 0;
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;

          case 'duration':
            aValue = audioDurations[a.routineId] || 0;
            bValue = audioDurations[b.routineId] || 0;
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;

          case 'songFileName':
            aValue = (a.songFileName || '').toLowerCase().trim();
            bValue = (b.songFileName || '').toLowerCase().trim();
            // Handle cases where one or both songs are missing
            if (!aValue && !bValue) return 0;
            if (!aValue) return sortConfig.direction === 'asc' ? 1 : -1;
            if (!bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            return sortConfig.direction === 'asc'
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);

          case 'averageAge':
            aValue = a.averageAge || 0;
            bValue = b.averageAge || 0;
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;

          default:
            return 0;
        }
      });
  }, [routines, searchQuery, sortConfig, audioDurations]);

  // Pagination calculations
  const totalItems = filteredRoutines.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = filteredRoutines.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortConfig]);

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key ? (prev.direction === "asc" ? "desc" : "asc") : "asc"
    }));
    setIsSortMenuOpen(false);
  };

  // CREATE Routine
  const handleCreateRoutine = async (routineData) => {
    try {
      // This function now only sends data to the backend and returns the response
      // It does NOT close the modal - let the modal handle its own closing logic
      const response = await fetchWithAuth(ROUTINES_API, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(routineData),
      });

      if (!response) {
        throw new Error("Failed to create routine");
      }

      // IMPORTANT: Return the newly created routine without closing modal
      // The modal will handle closing itself after the upload is complete
      return response;
    } catch (err) {
      console.error("Error creating routine:", err);
      throw err; // re-throw so the modal knows to handle error
    }
  };

  // UPDATE Routine - Modified version
  const handleUpdateRoutine = async (routineData) => {
    try {
      // Make the PUT call
      const updatedRoutine = await fetchWithAuth(
        `${ROUTINES_API}/${selectedRoutine.routineId}`,
        {
          method: "PUT",
          body: JSON.stringify(routineData),
        }
      );

      // Return the updated routine WITHOUT closing the modal or updating state
      // Let the modal handle its own closing logic
      return updatedRoutine;
    } catch (err) {
      console.error("Error updating routine:", err);
      setError(err.message);
      // rethrow so RoutineFormModal sees an error
      throw err;
    }
  };

  // Delete Routine
  const handleDeleteConfirm = async (routineId) => {
    try {
      await fetchWithAuth(`${ROUTINES_API}/${routineId}`, {
        method: "DELETE",
      });

      // Remove from local state
      setRoutines((prev) => prev.filter((r) => r.routineId !== routineId));

      setIsDeleteDialogOpen(false);
    } catch (err) {
      console.error("Error deleting routine:", err);
      setError(err.message);
    }
  };

  // Edit Routine Handler
  const handleEditRoutine = (routine) => {
    setSelectedRoutine(routine);
    setIsEditDialogOpen(true);
  };

  // Delete Routine Handler
  const handleDeleteRoutine = (routine) => {
    setSelectedRoutine(routine);
    setIsDeleteDialogOpen(true);
  };

  const handleRoutineComplete = (routine) => {
    console.log("Routine complete callback called with:", routine);

    // Now update the local state AFTER the upload is complete
    if (selectedRoutine) {
      // Update existing routine
      setRoutines((prev) =>
        prev.map((r) =>
          r.routineId === selectedRoutine.routineId ? routine : r
        )
      );
      setIsEditDialogOpen(false);
      setSelectedRoutine(null);
    } else {
      // Add new routine
      setRoutines((prev) => [...prev, routine]);
      setIsAddDialogOpen(false);
    }
  };

  // Bulk Selection Handlers
  const toggleRoutineSelection = (routineId) => {
    setSelectedRoutines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(routineId)) {
        newSet.delete(routineId);
      } else {
        newSet.add(routineId);
      }
      return newSet;
    });
  };

  const selectAllRoutines = () => {
    // Check if all items on current page are selected
    const currentPageSelected = currentPageItems.every(routine => 
      selectedRoutines.has(routine.routineId)
    );
    
    if (currentPageSelected && currentPageItems.length > 0) {
      // Unselect all items on current page
      const newSelected = new Set(selectedRoutines);
      currentPageItems.forEach(routine => {
        newSelected.delete(routine.routineId);
      });
      setSelectedRoutines(newSelected);
    } else {
      // Select all items on current page
      const newSelected = new Set(selectedRoutines);
      currentPageItems.forEach(routine => {
        newSelected.add(routine.routineId);
      });
      setSelectedRoutines(newSelected);
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Delete all selected routines in parallel
      await Promise.all(
        Array.from(selectedRoutines).map(routineId =>
          fetchWithAuth(`${ROUTINES_API}/${routineId}`, {
            method: "DELETE",
          })
        )
      );

      // Update local state
      setRoutines(prev => prev.filter(r => !selectedRoutines.has(r.routineId)));
      setSelectedRoutines(new Set());
      setIsBulkDeleteModalOpen(false);
    } catch (err) {
      console.error("Error deleting routines:", err);
      setError(err.message);
    }
  };

  // Clear selection when exiting selection mode
  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedRoutines(new Set());
  };

  // Clear selection when search query or page changes
  useEffect(() => {
    setSelectedRoutines(new Set());
    setIsSelectionMode(false);
  }, [searchQuery, currentPage]);

  // Error State
  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-10 md:pt-0">
          <div
            className={`p-4 rounded-lg ${
              isDarkMode
                ? "bg-red-500/10 border border-red-500/20 text-red-400"
                : "alert alert-error shadow-lg"
            }`}
          >
            <div className="flex items-center">
              <AlertCircle
                className={`h-6 w-6 ${
                  isDarkMode ? "text-red-400" : "stroke-current"
                } flex-shrink-0 mr-3`}
              />
              <span>{error}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-6 pt-10 md:pt-0">
      {/* Page Title Section */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className={`text-2xl md:text-3xl font-bold ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}>
            Routines
          </h1>
          <p className={`text-sm md:text-base ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}>
            Create and manage your dance routines, assign dancers, and upload music tracks.
          </p>
        </div>
        
        {!isSelectionMode && (
          <button
            onClick={() => setIsAddDialogOpen(true)}
            className={`md:hidden inline-flex items-center justify-center px-4 py-2 rounded-lg transition-all duration-200 ${
              isDarkMode
                ? "bg-blue-600 text-white hover:bg-blue-700 hover:scale-[1.02]"
                : "btn btn-primary gap-2"
            }`}
          >
            <Plus className="h-5 w-5" />
            <span>New Routine</span>
          </button>
        )}
      </div>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {isLoading && (
          <>
            <SkeletonStatCard />
            <SkeletonStatCard />
            <SkeletonStatCard />
          </>
        )}
        {!isLoading && (
          <>
            <div
              className={`rounded-xl shadow ${
                isDarkMode
                  ? "bg-card-gradient backdrop-blur-sm border border-brand-border-logo shadow-lg"
                  : "card bg-base-100"
              }`}
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div
                    className={`p-2 rounded-lg ${
                      isDarkMode ? "bg-blue-500/10" : "bg-primary/10"
                    }`}
                  >
                    <Music
                      className={`h-6 w-6 ${
                        isDarkMode ? "text-blue-400" : "text-primary"
                      }`}
                    />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt
                        className={`text-sm font-medium truncate ${
                          isDarkMode ? "text-gray-400" : "text-base-content/70"
                        }`}
                      >
                        Total Routines
                      </dt>
                      <dd
                        className={`text-lg font-medium ${
                          isDarkMode ? "text-white" : "text-base-content"
                        }`}
                      >
                        {routines.length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Roster Dancers Card */}
            <div
              className={`rounded-xl shadow ${
                isDarkMode
                  ? "bg-card-gradient backdrop-blur-sm border border-brand-border-logo shadow-lg"
                  : "card bg-base-100"
              }`}
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div
                    className={`p-2 rounded-lg ${
                      isDarkMode ? "bg-violet-500/10" : "bg-info/10"
                    }`}
                  >
                    <Users
                      className={`h-6 w-6 ${
                        isDarkMode ? "text-violet-400" : "text-info"
                      }`}
                    />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt
                        className={`text-sm font-medium truncate ${
                          isDarkMode ? "text-gray-400" : "text-base-content/70"
                        }`}
                      >
                        Roster Dancers
                      </dt>
                      <dd
                        className={`text-lg font-medium ${
                          isDarkMode ? "text-white" : "text-base-content"
                        }`}
                      >
                        {activeDancers}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Upcoming Events Card */}
            <div
              className={`rounded-xl shadow ${
                isDarkMode
                  ? "bg-card-gradient backdrop-blur-sm border border-brand-border-logo shadow-lg"
                  : "card bg-base-100"
              }`}
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div
                    className={`p-2 rounded-lg ${
                      isDarkMode ? "bg-green-500/10" : "bg-success/10"
                    }`}
                  >
                    <Calendar
                      className={`h-6 w-6 ${
                        isDarkMode ? "text-green-400" : "text-success"
                      }`}
                    />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt
                        className={`text-sm font-medium truncate ${
                          isDarkMode ? "text-gray-400" : "text-base-content/70"
                        }`}
                      >
                        Upcoming Events
                      </dt>
                      <dd
                        className={`text-lg font-medium ${
                          isDarkMode ? "text-white" : "text-base-content"
                        }`}
                      >
                        {routines.filter((r) => r.nextEvent).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Search Bar and Controls Row */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        {/* Search Bar */}
        <div className="relative flex-1 w-full">
          <input
            type="text"
            placeholder="Search routines by title or style..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pr-10 pl-4 py-3 rounded-lg ${
              isDarkMode
                ? "bg-[#1a1f2d] border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                : "input input-bordered"
            }`}
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Search
              className={`h-5 w-5 ${
                isDarkMode ? "text-gray-500" : "text-base-content/40"
              }`}
            />
          </div>
        </div>

        {/* Controls Group */}
        <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-end">
          {/* Sort Button with Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}
              className={`p-2 rounded-lg transition-colors group ${
                isDarkMode
                  ? "bg-white/5 hover:bg-white/10 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
              aria-label="Sort routines"
            >
              <ArrowDownUp className="h-5 w-5" />
              <span className={`absolute -bottom-8 left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity ${
                isDarkMode 
                  ? "bg-[#252D42] text-gray-300" 
                  : "bg-gray-800 text-white"
              }`}>
                Sort routines
              </span>
            </button>

            {isSortMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsSortMenuOpen(false)}
                />
                <div
                  className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg z-20 ${
                    isDarkMode
                      ? "bg-[#1a1f2d] border border-white/10"
                      : "bg-white border border-gray-200"
                  }`}
                >
                  <div className="py-1">
                    {[
                      { key: "title", label: "Title" },
                      { key: "dancers", label: "Number of Dancers" },
                      { key: "duration", label: "Duration" },
                      { key: "averageAge", label: "Average Age" },
                    ].map((option) => (
                      <button
                        key={option.key}
                        onClick={() => handleSort(option.key)}
                        className={`w-full px-4 py-2 text-sm text-left flex items-center justify-between ${
                          isDarkMode
                            ? "text-gray-300 hover:bg-white/5"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {option.label}
                        {sortConfig.key === option.key && (
                          <ArrowUpDown className="h-3 w-3 opacity-50" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* View Mode Toggle */}
          <div className={`flex items-center gap-2 p-1 rounded-lg ${
            isDarkMode ? "bg-white/5" : "bg-gray-100"
          }`}>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-lg transition-all ${
                viewMode === "list"
                  ? isDarkMode
                    ? "bg-blue-600/50 text-white"
                    : "bg-primary text-white"
                  : isDarkMode
                  ? "text-gray-500 hover:bg-white/5"
                  : "text-base-content/50 hover:bg-base-300"
              }`}
              title="List View"
            >
              <List className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-lg transition-all ${
                viewMode === "grid"
                  ? isDarkMode
                    ? "bg-blue-600/50 text-white"
                    : "bg-primary text-white"
                  : isDarkMode
                  ? "text-gray-500 hover:bg-white/5"
                  : "text-base-content/50 hover:bg-base-300"
              }`}
              title="Grid View"
            >
              <Grid className="h-5 w-5" />
            </button>
          </div>

          {/* Selection Mode Button */}
          <button
            onClick={() => {
              if (isSelectionMode) {
                setIsSelectionMode(false);
                setSelectedRoutines(new Set());
              } else {
                setIsSelectionMode(true);
              }
            }}
            className={`px-3 py-2 rounded-lg text-sm transition-colors ${
              isSelectionMode
                ? isDarkMode
                  ? "bg-blue-500/20 text-blue-400"
                  : "bg-blue-100 text-blue-700"
                : isDarkMode
                ? "bg-white/5 hover:bg-white/10 text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
          >
            {isSelectionMode ? "Cancel" : "Select"}
          </button>

          {/* Only show bulk action buttons when items are selected */}
          {isSelectionMode && selectedRoutines.size > 0 && (
            <>
              {/* Bulk Add Dancers Button */}
              <button
                onClick={() => {
                  setBulkDancerMode("add");
                  setIsBulkDancerModalOpen(true);
                }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDarkMode
                    ? "bg-green-500/10 text-green-400 hover:bg-green-500/20"
                    : "bg-green-100 text-green-700 hover:bg-green-200"
                }`}
              >
                Add Dancers ({selectedRoutines.size})
              </button>

              {/* Bulk Remove Dancers Button */}
              <button
                onClick={() => {
                  setBulkDancerMode("remove");
                  setIsBulkDancerModalOpen(true);
                }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDarkMode
                    ? "bg-orange-500/10 text-orange-400 hover:bg-orange-500/20"
                    : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                }`}
              >
                Remove Dancers ({selectedRoutines.size})
              </button>

              {/* Delete Button */}
              <button
                onClick={() => setIsBulkDeleteModalOpen(true)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDarkMode
                    ? "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                    : "bg-red-100 text-red-700 hover:bg-red-200"
                }`}
              >
                Delete Selected ({selectedRoutines.size})
              </button>
            </>
          )}

          {/* Hide New Routine button in selection mode and on mobile */}
          {!isSelectionMode && (
            <button
              onClick={() => setIsAddDialogOpen(true)}
              className={`hidden md:inline-flex items-center justify-center px-4 py-2 rounded-lg transition-all duration-200 ${
                isDarkMode
                  ? "bg-blue-600 text-white hover:bg-blue-700 hover:scale-[1.02]"
                  : "btn btn-primary gap-2"
              }`}
            >
              <Plus className="h-5 w-5" />
              <span>New Routine</span>
            </button>
          )}
        </div>
      </div>

      {/* Grid View */}
      {viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {currentPageItems.map((routine) => (
            <div
              key={routine.routineId}
              onClick={() => isSelectionMode && toggleRoutineSelection(routine.routineId)}
              className={`
                relative group rounded-xl overflow-hidden border transition-all cursor-pointer
                ${isDarkMode 
                  ? "border-white/10" 
                  : "border-gray-200"}
                ${isSelectionMode
                  ? selectedRoutines.has(routine.routineId)
                    ? isDarkMode
                      ? "ring-2 ring-blue-500 border-transparent"
                      : "ring-2 ring-blue-500 border-transparent bg-blue-50/80"
                    : "hover:border-blue-500/50"
                  : isDarkMode
                  ? "hover:border-white/20"
                  : "hover:border-gray-300"}
              `}
            >
              {/* Selection Overlay - Lighter for light mode */}
              {isSelectionMode && selectedRoutines.has(routine.routineId) && (
                <div className={`absolute inset-0 ${
                  isDarkMode ? "bg-blue-500/10" : "bg-blue-50/50"
                }`} />
              )}

              {/* Card Content */}
              <div className={`${isSelectionMode ? "pointer-events-none" : ""}`}>
                <RoutineCard
                  routine={routine}
                  roster={roster}
                  onEdit={() => handleEditRoutine(routine)}
                  onDelete={() => handleDeleteRoutine(routine)}
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table View */}
      {viewMode === "list" && (
        <div className={`overflow-x-auto rounded-lg border relative ${
          isDarkMode
            ? "bg-card-gradient backdrop-blur-sm border-brand-border-logo shadow-lg"
            : "bg-base-100 border-base-200"
        }`}>
          <table className="table w-full">
            <thead>
              <tr className={isDarkMode ? "border-brand-border-light" : ""}>
                {/* Number column */}
                <th className={isDarkMode ? "text-gray-400" : ""}>
                  <div className="flex items-center justify-center">
                    #
                  </div>
                </th>

                {/* Selection Checkbox Column */}
                {isSelectionMode && (
                  <th className="w-12 px-4 py-3">
                    <label className="cursor-pointer">
                      <input
                        type="checkbox"
                        className={`
                          h-4 w-4 rounded-md border-2 transition-all duration-200
                          ${isDarkMode 
                            ? "border-white/20 bg-white/5" 
                            : "border-gray-300 bg-white"}
                        `}
                        checked={currentPageItems.length > 0 && currentPageItems.every(routine => selectedRoutines.has(routine.routineId))}
                        onChange={selectAllRoutines}
                      />
                    </label>
                  </th>
                )}
                <th 
                  onClick={() => handleSort("title")}
                  className={`cursor-pointer group ${isDarkMode ? "text-gray-400" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    Routine
                    <ArrowUpDown className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                      sortConfig.key === "title" ? "opacity-100" : ""
                    }`} />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort("dancers")}
                  className={`cursor-pointer group ${isDarkMode ? "text-gray-400" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    Dancers
                    <ArrowUpDown className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                      sortConfig.key === "dancers" ? "opacity-100" : ""
                    }`} />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort("averageAge")}
                  className={`cursor-pointer group ${isDarkMode ? "text-gray-400" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    Avg. Age
                    <ArrowUpDown className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                      sortConfig.key === "averageAge" ? "opacity-100" : ""
                    }`} />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort("songFileName")}
                  className={`cursor-pointer group ${isDarkMode ? "text-gray-400" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    Song
                    <ArrowUpDown className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                      sortConfig.key === "songFileName" ? "opacity-100" : ""
                    }`} />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort("duration")}
                  className={`cursor-pointer group ${isDarkMode ? "text-gray-400" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    Duration
                    <ArrowUpDown className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                      sortConfig.key === "duration" ? "opacity-100" : ""
                    }`} />
                  </div>
                </th>
                <th className={isDarkMode ? "text-gray-400" : ""}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentPageItems.map((routine, index) => (
                <tr
                  key={routine.routineId}
                  onClick={() => isSelectionMode && toggleRoutineSelection(routine.routineId)}
                  className={`
                    group transition-colors
                    ${isSelectionMode ? "cursor-pointer" : ""}
                    ${selectedRoutines.has(routine.routineId)
                      ? isDarkMode
                        ? "bg-blue-500/10"
                        : "bg-blue-50/80"
                      : isDarkMode 
                        ? "hover:bg-white/5" 
                        : "hover:bg-gray-50"}
                    ${index !== filteredRoutines.length - 1 
                      ? isDarkMode 
                        ? "border-b border-white/10" 
                        : "border-b border-gray-200"
                      : ""}
                  `}
                >
                  {/* Number */}
                  <td className={`text-center font-mono text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    {startIndex + index + 1}
                  </td>

                  {/* Selection Checkbox */}
                  {isSelectionMode && (
                    <td className="w-12 px-4 py-3">
                      <label className="cursor-pointer">
                        <input
                          type="checkbox"
                          className={`
                            h-4 w-4 rounded-md border-2 transition-all duration-200
                            ${isDarkMode 
                              ? "border-white/20 bg-white/5" 
                              : "border-gray-300 bg-white"}
                          `}
                          checked={selectedRoutines.has(routine.routineId)}
                          onChange={(e) => e.stopPropagation()}
                        />
                      </label>
                    </td>
                  )}
                  <RoutineTableRow
                    routine={routine}
                    roster={roster}
                    onEdit={handleEditRoutine}
                    onDelete={handleDeleteRoutine}
                    isDarkMode={isDarkMode}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className={`flex flex-col sm:flex-row justify-between items-center gap-4 mt-4 p-4 rounded-lg ${
          isDarkMode 
            ? "bg-brand-card-bg/80 backdrop-blur-sm border border-brand-border-light" 
            : "bg-base-100 border border-base-200"
        }`}>
          {/* Items per page selector */}
          <div className="flex items-center gap-2">
            <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              Show:
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className={`select select-sm select-bordered ${
                isDarkMode 
                  ? "bg-[#1a1f2d] border-white/10 text-white" 
                  : ""
              }`}
            >
              <option value={4}>4</option>
              <option value={8}>8</option>
              <option value={12}>12</option>
              <option value={16}>16</option>
              <option value={20}>20</option>
            </select>
            <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              per page
            </span>
          </div>

          {/* Page info */}
          <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} routines
          </div>

          {/* Page navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`btn btn-sm btn-ghost ${
                currentPage === 1 ? "btn-disabled" : ""
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            {/* Page numbers */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`btn btn-sm ${
                      currentPage === pageNumber
                        ? "btn-primary"
                        : `btn-ghost ${isDarkMode ? "hover:bg-white/10" : ""}`
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`btn btn-sm btn-ghost ${
                currentPage === totalPages ? "btn-disabled" : ""
              }`}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsBulkDeleteModalOpen(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className={`
              relative w-full max-w-md p-6 rounded-xl
              ${isDarkMode 
                ? "bg-[#1a1f2d] border border-white/10" 
                : "bg-white border border-base-300"}
            `}>
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center
                    ${isDarkMode ? "bg-red-500/10" : "bg-error/10"}
                  `}>
                    <Trash className={`w-6 h-6 ${isDarkMode ? "text-red-400" : "text-error"}`} />
                  </div>
                </div>

                <div className="text-center space-y-2">
                  <h3 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>
                    Delete {selectedRoutines.size} Routine{selectedRoutines.size !== 1 ? "s" : ""}?
                  </h3>
                  <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
                    This action cannot be undone. These routines will be permanently deleted.
                  </p>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setIsBulkDeleteModalOpen(false)}
                    className={`
                      btn btn-ghost btn-sm normal-case
                      ${isDarkMode 
                        ? "text-gray-400 hover:text-white hover:bg-white/5" 
                        : "text-gray-600 hover:text-gray-900"}
                    `}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className={`
                      btn btn-sm normal-case
                      ${isDarkMode 
                        ? "bg-red-500 hover:bg-red-600 border-0 text-white" 
                        : "btn-error"}
                    `}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <RoutineFormModal
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        routine={newRoutineTemplate}
        onSubmit={handleCreateRoutine}
        onComplete={handleRoutineComplete}
        isEditMode={false}
        roster={roster}
        isDarkMode={isDarkMode}
      />

      {selectedRoutine && (
        <RoutineFormModal
          isOpen={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          routine={selectedRoutine}
          onSubmit={handleUpdateRoutine}
          onComplete={handleRoutineComplete}
          isEditMode={true}
          roster={roster}
          isDarkMode={isDarkMode}
        />
      )}

      <DeleteModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        routine={selectedRoutine}
        onDelete={() => handleDeleteConfirm(selectedRoutine.routineId)}
        isDarkMode={isDarkMode}
      />

      {/* Bulk Dancer Management Modal */}
      <BulkDancerModal
        isOpen={isBulkDancerModalOpen}
        onClose={() => setIsBulkDancerModalOpen(false)}
        mode={bulkDancerMode}
        selectedRoutines={Array.from(selectedRoutines).map(id => 
          routines.find(r => r.routineId === id)
        ).filter(Boolean)}
        roster={roster}
        isDarkMode={isDarkMode}
        onSuccess={() => {
          setIsBulkDancerModalOpen(false);
          setIsSelectionMode(false);
          setSelectedRoutines(new Set());
          // Refresh routines data to show updated dancer counts
          fetchData();
        }}
      />
      </div>
    </div>
  );
};

export default RoutinesPage;

// The code below this point appears to be incomplete and causing syntax errors
// Removing incomplete RoutineListItem component to fix the syntax error